import type { Product } from '@/types'

/**
 * Resolve a scanned or typed code to exactly one product.
 *
 * Barcode wins over SKU. They are separate namespaces — a barcode is what the
 * manufacturer printed, a SKU is what the shop calls it — and a shop that
 * reuses a supplier's barcode digits as its own SKU for a *different* product
 * would otherwise ring up the wrong item. Barcode is matched exactly (the
 * scanner sends what is printed); SKU is matched case-insensitively because
 * staff type it by hand.
 *
 * Returns null when nothing matches or when the match is ambiguous, so the
 * caller falls back to the normal search list rather than guessing. Ambiguity
 * should be impossible — both columns are uniquely indexed — but a cached
 * product list can lag a rename, and silently picking one of two products is
 * the one outcome a till must never have.
 */
export function resolveScanMatch(products: Product[], rawCode: string): Product | null {
  const code = rawCode.trim()
  if (code === '') return null

  const byBarcode = products.filter((p) => (p.barcode ?? '').trim() === code)
  if (byBarcode.length === 1) return byBarcode[0]
  if (byBarcode.length > 1) return null

  const lower = code.toLowerCase()
  const bySku = products.filter((p) => (p.sku ?? '').trim().toLowerCase() === lower)
  if (bySku.length === 1) return bySku[0]

  return null
}

/**
 * Whether this product can be added to the cart right now.
 *
 * `blockOversell` is the tenant's opt-in policy. When it is off (the default) a
 * product with no stock still rings — the shop can see the item in their hand,
 * and the count is what is wrong. The server enforces the same rule; this is
 * only so the cashier finds out before the customer is waiting.
 */
export function canAddToCart(
  product: Product,
  quantityAlreadyInCart: number,
  blockOversell: boolean,
): { ok: true } | { ok: false; reason: string } {
  if (!product.is_available) {
    return { ok: false, reason: `${product.name} is marked unavailable.` }
  }
  if (!blockOversell || product.kind !== 'stock') return { ok: true }

  const onHand = product.quantity_on_hand ?? 0
  if (onHand - quantityAlreadyInCart < 1) {
    return {
      ok: false,
      reason:
        onHand <= 0
          ? `${product.name} is out of stock.`
          : `Only ${onHand} ${product.unit || 'each'} of ${product.name} left.`,
    }
  }
  return { ok: true }
}
